import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ModuleConsoleFooter } from "@/components/composition/ModuleConsoleFooter";
import { PageHeader } from "@/components/PageHeader";
import { t } from "@/core/composition/lexicon";
import { pharmacyInventoryApi, type StockReceiptLedgerRow } from "@/core/inventory/inventory-api";
import { IscApiError } from "@/core/inventory/isc-client";
import {
  depotSupplyRoutes,
  STOCK_DEPOTS,
  type StockDepotId,
} from "@/core/inventory/stock-depots";
import { formatInvoiceDate } from "@/core/pharmacy/format";
import { routes } from "@/router/all_routes";

function isApiOfflineError(err: unknown): boolean {
  return err instanceof IscApiError && err.status === 0;
}

export function SupplyReceiptsHistoryPage({ depot }: { depot: StockDepotId }) {
  const depotConfig = STOCK_DEPOTS[depot];
  const depotRoutes = depotSupplyRoutes(depot);
  const [rows, setRows] = useState<StockReceiptLedgerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const storeId = await pharmacyInventoryApi.getDepotStoreId(depot);
      const result = await pharmacyInventoryApi.listReceiptLedger({
        page,
        limit: 30,
        storeId: storeId ?? undefined,
      });
      setRows(result.items);
      setTotalPages(result.meta.totalPages);
    } catch (err) {
      setError(
        isApiOfflineError(err)
          ? "API stock inaccessible — démarrez itaas-inventory."
          : err instanceof Error
            ? err.message
            : "Chargement impossible.",
      );
    } finally {
      setLoading(false);
    }
  }, [depot, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="ph-dashboard">
      <PageHeader
        title={`${depotConfig.label} — historique réceptions`}
        description={`Journal des entrées stock ISC (mouvements RECEIPT) — dépôt ${depotConfig.storeCode}.`}
        actions={
          <div className="d-flex gap-2 ph-page-header-actions">
            <Link to={depotRoutes.receipts} className="btn btn-primary btn-sm">
              Nouvelle réception
            </Link>
            <Link to={depotRoutes.levels} className="btn btn-outline-secondary btn-sm">
              {t("supply.levels")}
            </Link>
          </div>
        }
      />

      <div className="ph-dash-card">
        {loading && <p className="small text-muted mb-0">Chargement du journal…</p>}
        {error && <div className="alert alert-warning py-2 small">{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <p className="text-muted mb-0">
            Aucune réception enregistrée. Utilisez{" "}
            <Link to={depotRoutes.receipts}>{t("supply.receipts")}</Link> pour la première entrée.
          </p>
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table table-sm ph-dash-table ph-diag-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Article</th>
                    <th>Code</th>
                    <th>Entrepôt</th>
                    <th className="text-end">Qté</th>
                    <th>Référence</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="ph-diag-row">
                      <td className="small text-nowrap" data-label="Date">
                        {formatInvoiceDate(row.postedAt)}
                      </td>
                      <td className="fw-medium" data-label="Article">
                        {row.productName}
                      </td>
                      <td className="small text-muted" data-label="Code">
                        {row.productCode}
                      </td>
                      <td className="small" data-label="Entrepôt">
                        {row.storeName}
                      </td>
                      <td className="text-end" data-label="Qté">
                        {row.quantity}
                      </td>
                      <td className="small" data-label="Référence">
                        <code>{row.reference}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Précédent
                </button>
                <span className="small text-muted">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ModuleConsoleFooter consoleRoute={routes.supplyAdminConsole} moduleLabel="ITaaS Inventory" />
    </div>
  );
}

export function SupplyMedicinesReceiptsHistoryPage() {
  return <SupplyReceiptsHistoryPage depot="pharmacy" />;
}

export function SupplyConsumablesReceiptsHistoryPage() {
  return <SupplyReceiptsHistoryPage depot="consumables" />;
}
